import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { UnitsGridColumn } from 'src/app/shared/components/units-grid-input/units-grid-input.models';

export interface ConnectionCqePoint {
  readonly axialLoad: number;
  readonly pressure: number;
}

export function createConnectionCqeColumns(forceUnit: string, pressureUnit: string): readonly UnitsGridColumn[] {
  return [
    {
      kind: 'number',
      field: 'axialLoad',
      header: 'Axial Load',
      unitSymbol: forceUnit,
      minFractionDigits: 2,
      maxFractionDigits: 6,
      formatFractionDigits: 3
    },
    {
      kind: 'number',
      field: 'pressure',
      header: 'Pressure',
      unitSymbol: pressureUnit,
      minFractionDigits: 2,
      maxFractionDigits: 6,
      formatFractionDigits: 3
    }
  ];
}

export function createConnectionCqeRow(data?: Partial<ConnectionCqePoint>): FormGroup {
  return new FormGroup({
    axialLoad: new FormControl<number | null>(data?.axialLoad ?? 0, [Validators.required]),
    pressure: new FormControl<number | null>(data?.pressure ?? null, [Validators.required])
  });
}

export function isConnectionCqeRowEmpty(row: FormGroup): boolean {
  const axialLoad = row.get('axialLoad')?.value;
  const pressure = row.get('pressure')?.value;
  const isAxialLoadEmpty = axialLoad === null || axialLoad === undefined || axialLoad === '' || Number.isNaN(Number(axialLoad)) || Number(axialLoad) === 0;
  const isPressureEmpty = pressure === null || pressure === undefined || pressure === '' || Number.isNaN(Number(pressure));
  return isAxialLoadEmpty && isPressureEmpty;
}

export function ensureTrailingInputRow(formArray: FormArray<FormGroup>): void {
  if (formArray.length === 0) {
    formArray.push(createConnectionCqeRow());
    return;
  }

  while (
    formArray.length > 1 &&
    isConnectionCqeRowEmpty(formArray.at(formArray.length - 1)) &&
    isConnectionCqeRowEmpty(formArray.at(formArray.length - 2))
  ) {
    formArray.removeAt(formArray.length - 1);
  }

  if (!isConnectionCqeRowEmpty(formArray.at(formArray.length - 1))) {
    formArray.push(createConnectionCqeRow());
  }
}

export function populateConnectionCqeFormArray(
  formArray: FormArray<FormGroup>,
  points: readonly ConnectionCqePoint[]
): void {
  formArray.clear();
  points.forEach((point) => formArray.push(createConnectionCqeRow(point)));
  ensureTrailingInputRow(formArray);
}

export function extractConnectionCqeRows(formArray: FormArray<FormGroup>): ConnectionCqePoint[] {
  return formArray.controls
    .filter((control) => !isConnectionCqeRowEmpty(control as FormGroup))
    .map((control) => ({
      axialLoad: Number(control.get('axialLoad')?.value ?? 0),
      pressure: Number(control.get('pressure')?.value ?? 0)
    }));
}